/**
 * Trip lock: editing the trip plan is disabled while a packing operation linked to that trip is active.
 * Packing list lock (see lockedEventIds): only events that are sources or targets of an active operation.
 */

import { useMemo } from 'react';

/** Event Inventory → packing list when this event is in the active operation’s source/target set. */
export const PACKING_LIST_LOCK_MESSAGE =
    'A packing operation is in progress that uses this event as a source or destination. Packing list quantities must stay unchanged until the operation is completed or canceled, because move tasks are based on them.';

/** Trip detail / edit trip / supply flow / recalculate while a linked operation is active. */
export const TRIP_PLAN_LOCK_MESSAGE =
    'A packing operation linked to this trip is in progress. Complete or cancel that operation before editing the trip, recalculating the plan, or changing supply flow so the plan and field tasks stay aligned.';
import { queryPackingOperations } from '../schema';

/**
 * lockedTripIds — trips that have an active packing operation (plan/route edits disabled).
 * lockedEventIds — events that are a source or target of an active operation (packing list edits disabled).
 */
export function useTripLock(): { lockedTripIds: Set<string>; lockedEventIds: Set<string> } {
    const { data } = queryPackingOperations();
    return useMemo(() => {
        const lockedTripIds = new Set<string>();
        const lockedEventIds = new Set<string>();
        const ops = data?.packingOperations ?? [];
        for (const op of ops) {
            if ((op as { status?: string }).status !== 'active') continue;
            const trip = (op as { trip?: { id?: string } | null }).trip;
            if (trip?.id) lockedTripIds.add(trip.id);
            const sourceEvents = (op as { sourceEvents?: Array<{ id: string }> }).sourceEvents ?? [];
            const targetEvents = (op as { targetEvents?: Array<{ id: string }> }).targetEvents ?? [];
            for (const e of sourceEvents) lockedEventIds.add(e.id);
            for (const e of targetEvents) lockedEventIds.add(e.id);
        }
        return { lockedTripIds, lockedEventIds };
    }, [data]);
}
